var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Gzip } from "../domtools/compression/native";
import { Zstd } from "../domtools/compression/zstd";
import { log } from "../utils";
import { PlayerBuffer } from "./buffer";
import { LiveTimer, PlaybackTimer } from "./timer";
export class Player {
    constructor(renderer, buffer = new PlayerBuffer(), options = {}) {
        var _a, _b;
        var _c, _d;
        this.renderer = renderer;
        this.buffer = buffer;
        this.options = options;
        this.startTime = 0;
        this.isGoingto = false;
        this.timer = null;
        this.isLive = true;
        this.playing = Promise.resolve();
        (_a = (_c = this.options).progress) !== null && _a !== void 0 ? _a : (_c.progress = () => { });
        (_b = (_d = this.options).ended) !== null && _b !== void 0 ? _b : (_d.ended = () => { });
    }
    load(sessionUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            this.pause();
            const data = yield (yield fetch(sessionUrl)).text();
            const session = data
                .split("\n")
                .filter((event) => event.length)
                .map((event) => JSON.parse(event))
                .sort((a, b) => a.time - b.time);
            this.buffer = new PlayerBuffer(Infinity, session);
            if (this.buffer.firstPaint === Infinity) {
                throw new Error("Recording has no useful content");
            }
            yield Zstd.load(false, true);
            this.renderer.options.compressionSupport.zstd.decompress =
                Zstd.canDecompress;
            this.renderer.options.compressionSupport.gzip.decompress =
                Gzip.canDecompress;
            this.timer = null;
            this.isGoingto = false;
            this.isLive = false;
            this.renderer.isLive = false;
            this.startTime = this.buffer.firstPaint;
        });
    }
    pause() {
        var _a;
        (_a = this.timer) === null || _a === void 0 ? void 0 : _a.pause();
    }
    play() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.timer) {
                this.timer = this.isLive
                    ? new LiveTimer(this.buffer)
                    : new PlaybackTimer(this.buffer);
            }
            else if (!this.timer.paused) {
                return;
            }
            yield this.playing;
            // timer instance might change in goto
            let timer = this.timer;
            this.timer.resume();
            let resolved;
            this.playing = new Promise((resolve) => (resolved = resolve));
            while (true) {
                const tick = yield timer.next();
                // timer was reset, skip beat
                if (timer !== this.timer) {
                    timer = this.timer;
                    continue;
                }
                if (!tick) {
                    if (timer.ended) {
                        this.options.ended();
                    }
                    break;
                }
                try {
                    if (tick.events.length > 1) {
                        yield this.renderer.render(Object.assign(Object.assign({}, tick.events[0]), { type: "Batch", payload: { events: tick.events } }));
                    }
                    else if (tick.events.length === 1) {
                        yield this.renderer.render(tick.events[0]);
                    }
                    else if (Number.isFinite(tick.time)) {
                        this.options.progress(tick.time - this.startTime);
                    }
                }
                catch (ex) {
                    console.warn(ex);
                }
            }
            resolved();
        });
    }
    /**
     * goto from 0 to `duration`
     * @param time time in ms
     */
    goto(time) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (this.isGoingto) {
                return;
            }
            // check for out of bounds
            time = Math.min(Math.max(time, 0) + this.startTime, this.buffer.at(this.buffer.length - 1).time);
            const playing = ((_a = this.timer) === null || _a === void 0 ? void 0 : _a.paused) === false;
            this.isGoingto = true;
            this.pause();
            yield this.playing;
            const oldIndex = this.buffer.index;
            try {
                this.buffer.seek(time);
                if (oldIndex !== this.buffer.index) {
                    const events = this.buffer.events.slice(oldIndex <= this.buffer.index ? oldIndex : 0, this.buffer.index);
                    try {
                        yield this.renderer.render(Object.assign(Object.assign({}, events[0]), { type: "Batch", payload: { events } }));
                    }
                    catch (ex) {
                        console.warn(ex);
                    }
                    this.options.progress(Math.trunc(time - this.startTime));
                }
            }
            catch (error) {
                log(error);
            }
            finally {
                this.isGoingto = false;
            }
            this.timer = new PlaybackTimer(this.buffer, time);
            this.timer.pause();
            if (playing) {
                this.play();
            }
        });
    }
    getEventVersion(event) {
        if (event.endpoint) {
            return 3;
        }
        else if ("document" in event) {
            return 2;
        }
        else {
            return 1;
        }
    }
    getDuration() {
        return this.buffer.lastTime - this.buffer.firstPaint;
    }
    getEventSummary() {
        if (!this.buffer.events.length) {
            return [];
        }
        else if (this.getEventVersion(this.buffer.events[0]) === 3) {
            return this.getEventSummaryV3();
        }
        else {
            return this.getEventSummaryV2();
        }
    }
    getEventSummaryV3() {
        // targets per document id
        const seekTargets = {};
        const foundTargets = {};
        const users = {};
        return this.buffer.events
            .reduceRight((acc, event) => {
            var _a, _b, _c, _d, _e;
            var _f, _g, _h;
            // at first, find the target elements these events act on
            switch (event.type) {
                case "PageMetadata":
                    if (event.payload.type === "UserInfo") {
                        acc.push(event);
                        users[event.payload.data.endpoint] = event.payload.data;
                    }
                    break;
                case "InputUpdate": {
                    const last = acc[acc.length - 1];
                    if (!event.payload.init &&
                        (!last ||
                            last.type !== event.type ||
                            last.payload.target !== event.payload.target)) {
                        (_a = seekTargets[_f = event.document]) !== null && _a !== void 0 ? _a : (seekTargets[_f] = {});
                        seekTargets[event.document][((_b = event.payload.selected) === null || _b === void 0 ? void 0 : _b.length)
                            ? event.payload.selected[0]
                            : event.payload.target] = true;
                        acc.push(event);
                    }
                    break;
                }
                case "Cursor": {
                    const last = acc[acc.length - 1];
                    if (event.payload.type === "click" &&
                        (!last || (last === null || last === void 0 ? void 0 : last.payload.target) !== event.payload.target)) {
                        (_c = seekTargets[_g = event.document]) !== null && _c !== void 0 ? _c : (seekTargets[_g] = {});
                        seekTargets[event.document][event.payload.target] = true;
                        acc.push(event);
                    }
                    break;
                }
                case "Mutations":
                    if (event.document in seekTargets &&
                        ((_d = event.payload.nodes) === null || _d === void 0 ? void 0 : _d.insert.length)) {
                        for (const id in seekTargets[event.document]) {
                            for (const fragment of event.payload.nodes.insert) {
                                if (id in fragment.nodes) {
                                    (_e = foundTargets[_h = event.document]) !== null && _e !== void 0 ? _e : (foundTargets[_h] = {});
                                    foundTargets[event.document][id] = fragment.nodes[id];
                                    delete seekTargets[event.document][id];
                                }
                            }
                        }
                    }
                    break;
            }
            return acc;
        }, [])
            .map((event) => {
            var _a, _b, _c, _d, _e, _f;
            switch (event.type) {
                case "PageMetadata": {
                    return {
                        type: "UserJoined",
                        time: event.time - this.startTime,
                        timestamp: event.time,
                        user: users[event.endpoint],
                    };
                }
                case "InputUpdate": {
                    let name;
                    const optionId = ((_a = event.payload.selected) === null || _a === void 0 ? void 0 : _a[0]) || 0;
                    const target = foundTargets[event.document][optionId || event.payload.target];
                    if (!target) {
                        return null;
                    }
                    else if ((_b = target.attrs) === null || _b === void 0 ? void 0 : _b.placeholder) {
                        name = `[placeholder=${target.attrs.placeholder}]`;
                    }
                    else if ((_c = target === null || target === void 0 ? void 0 : target.attrs) === null || _c === void 0 ? void 0 : _c.type) {
                        name = `[type=${target.attrs.type}]`;
                    }
                    else {
                        name = ":" + target.name.toLocaleLowerCase();
                    }
                    return {
                        type: "Input",
                        time: event.time - this.startTime,
                        timestamp: event.time,
                        user: users[event.endpoint],
                        value: optionId
                            ? (_d = target.attrs) === null || _d === void 0 ? void 0 : _d.value
                            : event.payload.value || event.payload.checked,
                        name,
                    };
                }
                case "Cursor": {
                    let name;
                    const target = foundTargets[event.document][event.payload.target];
                    const payload = event.payload;
                    if (!target) {
                        return null;
                    }
                    else if (payload.innerTextPrefix &&
                        payload.innerTextPrefix.length) {
                        name = `${target.name.toLocaleLowerCase()}[text=${payload.innerTextPrefix}]`;
                    }
                    else if (target.name === "IMG" && ((_e = target.attrs) === null || _e === void 0 ? void 0 : _e.alt)) {
                        name = `${target.name.toLocaleLowerCase()}[alt=${target.attrs.alt}]`;
                    }
                    else if ((_f = target.attrs) === null || _f === void 0 ? void 0 : _f.id) {
                        name = `${target.name.toLocaleLowerCase()}[id=${target.attrs.id}]`;
                    }
                    else {
                        name = target.name || "";
                    }
                    return {
                        type: "Click",
                        time: event.time - this.startTime,
                        timestamp: event.time,
                        user: users[event.endpoint],
                        name,
                    };
                }
                default:
                    return null;
            }
        })
            .filter((ev) => ev)
            .reverse();
    }
    getEventSummaryV2() {
        const seekTargets = {};
        const foundTargets = {};
        const users = {};
        return this.buffer.events
            .reduceRight((acc, event) => {
            switch (event.type) {
                case "PageMetadata": {
                    if (event.payload.type === "UserInfo") {
                        acc.push(event);
                        users[event.payload.data.id] = event.payload.data;
                    }
                    break;
                }
                case "InputUpdate": {
                    const last = acc[acc.length - 1];
                    if (!last ||
                        last.type !== event.type ||
                        last.payload.target !== event.payload.target) {
                        seekTargets[event.payload.target] = true;
                        acc.push(event);
                    }
                    break;
                }
                case "Cursor": {
                    const last = acc[acc.length - 1];
                    if (event.payload.type === "click" &&
                        (!last || (last === null || last === void 0 ? void 0 : last.payload.target) !== event.payload.target)) {
                        seekTargets[event.payload.target] = true;
                        acc.push(event);
                    }
                    break;
                }
                case "TreeInstance":
                case "Observer": {
                    for (const target in seekTargets) {
                        if (seekTargets[target] &&
                            target in event.payload.records &&
                            event.payload.records[target].type === "insert") {
                            foundTargets[target] = event.payload.records[target];
                            seekTargets[target] = false;
                        }
                    }
                    break;
                }
            }
            return acc;
        }, [])
            .map((event) => {
            var _a, _b, _c, _d;
            switch (event.type) {
                case "PageMetadata": {
                    return {
                        type: "UserJoined",
                        time: event.time - this.startTime,
                        timestamp: event.time,
                        user: users[event.user.id],
                    };
                }
                case "InputUpdate": {
                    let name;
                    const target = foundTargets[event.payload.target];
                    if ((_a = target === null || target === void 0 ? void 0 : target.node.attrs) === null || _a === void 0 ? void 0 : _a.placeholder) {
                        name = `[placeholder=${target.node.attrs.placeholder}]`;
                    }
                    else if ((_b = target === null || target === void 0 ? void 0 : target.node.attrs) === null || _b === void 0 ? void 0 : _b.type) {
                        name = `[type=${target.node.attrs.type}]`;
                    }
                    return {
                        type: "Input",
                        time: event.time - this.startTime,
                        timestamp: event.time,
                        user: users[event.user.id],
                        value: event.payload.value || event.payload.checked,
                        name,
                    };
                }
                case "Cursor": {
                    let name;
                    const target = foundTargets[event.payload.target];
                    if (event.payload.innerTextPrefix &&
                        event.payload.innerTextPrefix.length) {
                        name = `${target === null || target === void 0 ? void 0 : target.node.name.toLocaleLowerCase()}[text=${event.payload.innerTextPrefix}${event.payload.innerTextPrefix.length > 50 ? "..." : ""}]`;
                    }
                    else if ((target === null || target === void 0 ? void 0 : target.node.name) === "IMG" && ((_c = target === null || target === void 0 ? void 0 : target.node.attrs) === null || _c === void 0 ? void 0 : _c.alt)) {
                        name = `${target === null || target === void 0 ? void 0 : target.node.name.toLocaleLowerCase()}[alt=${target.node.attrs.alt}]`;
                    }
                    else if ((_d = target === null || target === void 0 ? void 0 : target.node.attrs) === null || _d === void 0 ? void 0 : _d.id) {
                        name = `${target === null || target === void 0 ? void 0 : target.node.name.toLocaleLowerCase()}[id=${target.node.attrs.id}]`;
                    }
                    else {
                        name = (target === null || target === void 0 ? void 0 : target.node.name) || "";
                    }
                    return {
                        type: "Click",
                        time: event.time - this.startTime,
                        timestamp: event.time,
                        user: users[event.user.id],
                        name,
                    };
                }
                default:
                    return event;
            }
        })
            .reverse();
    }
}
//# sourceMappingURL=index.js.map